'use client';

import { useState } from 'react';

const faqItems: { question: string; answer: string }[] = [
  {
    question: 'What is Tally?',
    answer:
      'Tally is a commission recovery and revenue-tracking platform built for Medicare agents and agencies. It reconciles your carrier statements against your actual bank deposits, flags missing or incorrect payments, and gives you dispute-ready evidence so you get paid what you earned.',
  },
  {
    question: 'Who is Tally for?',
    answer:
      'Tally is built by Medicare brokers for independent brokers and agencies. Whether you’re a solo agent or a growing team, Tally helps you keep a clean commission source of truth and recover revenue that would otherwise be lost to leakage, clerical errors, or disenrollments.',
  },
  {
    question: 'How does Tally work?',
    answer:
      'You connect your carrier reports and bank data (via uploads or secure connections). Tally’s algorithm verifies every member, plan code, and payment date in real time. It maps carrier lines to bank deposits, flags variances, and builds an auditable ledger. You see what was paid, what’s pending, and what’s on the recovery list, all in one dashboard you can trust.',
  },
  {
    question: 'What problems does Tally solve?',
    answer:
      'Between rapid disenrollments and clerical errors, about 10% of agent revenue is lost to leakage. Tally identifies missing payments, underpayments, and chargebacks. It replaces hours of manual cross-referencing with a one-click audit and gives you the proof you need to win disputes with carriers.',
  },
  {
    question: 'What features does Tally offer?',
    answer:
      'Core features include commission tracking, carrier reconciliation, payment match reports, and monthly data exports. Luxe adds automated carrier connections, advanced audits, automated payment match, state-smart FMV rate detection, and priority revenue recovery. Your book of business is organized by revenue so you know exactly what your day was worth.',
  },
  {
    question: 'What’s the difference between Basic and Luxe?',
    answer:
      'Basic is manual-upload only (no carrier sync) and includes core tracking, basic payment match, and exports. Luxe includes everything in Basic plus automated carrier connections, advanced launch data audit, automated payment match, state-smart FMV detection, and priority revenue recovery. Start simple and grow into automation when you’re ready.',
  },
  {
    question: 'What is the “first 50” price lock?',
    answer:
      'Be one of the first 50 members and we lock in your current rate forever. As we add more features and automation, your price will never move. No tiers, no hidden fees, just the only commission tool you’ll ever have to buy.',
  },
  {
    question: 'Can I switch plans or cancel?',
    answer:
      'Yes. Start simple and grow into automation when you’re ready. Switch plans or cancel anytime.',
  },
  {
    question: 'What’s included in all plans?',
    answer:
      'All plans include secure authentication, carrier statement storage, and exportable ledgers.',
  },
  {
    question: 'How do I get started?',
    answer:
      'Click “Get early access” or “Get Early Access & Lock in Founder Pricing” anywhere on this page. You’ll choose your plan (Basic or Luxe) and complete sign-up. From there you can connect carriers, upload statements, and start reconciling your commissions.',
  },
];

export default function MarketingFAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section id="faq" className="mt-32 md:mt-36 pt-10 pb-10 flex flex-col items-center">
      <div className="w-full max-w-2xl px-4">
        <h2 className="font-poppins text-[32px] md:text-[53px] font-bold sm:font-semibold tracking-tight leading-[1.2] text-black text-center mb-[40px]">
          Frequently asked questions
        </h2>
        <div className="space-y-4">
          {faqItems.map((item, index) => (
            <div key={index} className="border-0 overflow-hidden">
              <button
                type="button"
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full py-4 text-left flex items-center justify-between gap-4 transition-colors"
              >
                <span className="font-poppins font-semibold text-slate-900 text-base md:text-lg">{item.question}</span>
                <span
                  className={`flex-shrink-0 text-slate-500 transition-transform ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </span>
              </button>
              {openIndex === index && (
                <div className="pb-4 pt-0">
                  <p className="text-slate-600 text-base md:text-lg leading-relaxed">{item.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
